import type { RouteRecord } from "vite-react-ssg";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { Layout } from "@/Layout";
import { LangRoute } from "@/components/LangRoute";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
// Index stays eagerly imported - it's the landing page, loaded on every
// visit, and prerendered by SSG. Other teasers are lazy-loaded below so
// their deps (zod, react-hook-form, radix menus, etc.) don't bloat the
// critical bundle served on `/`.
import Index from "@/pages/Index";
import NotFound from "@/pages/NotFound";
import { GUIDES, type GuideId } from "@/lib/guide-routes";
import { accountRoute } from "@/routes/account";

const lazyMobileApp = async () => ({ Component: (await import("@/pages/MobileApp")).default });
const lazyAppRedirect = async () => ({ Component: (await import("@/pages/AppRedirect")).default });
const lazyContact = async () => ({ Component: (await import("@/pages/Contact")).default });
const lazyTerms = async () => ({ Component: (await import("@/pages/Terms")).default });
const lazyPrivacy = async () => ({ Component: (await import("@/pages/Privacy")).default });
const lazyReturns = async () => ({ Component: (await import("@/pages/Returns")).default });
const lazyDeleteAppAccount = async () => ({ Component: (await import("@/pages/DeleteAppAccount")).default });
const lazyFaq = async () => ({ Component: (await import("@/pages/Faq")).default });
const lazyGuides = async () => ({ Component: (await import("@/pages/Guides")).default });

// Long-form SEO guides (Phase 3B). One lazy import per guide id. Slugs
// differ per language, so these routes are registered explicitly per
// language tree below rather than via the `teaserChildren` multiplier.
const guideLazyLoaders: Record<GuideId, () => Promise<{ Component: React.ComponentType }>> = {
  "how-hive-scale-works": async () => ({
    Component: (await import("@/pages/guides/HowHiveScaleWorks")).default,
  }),
  "apiary-monitoring": async () => ({
    Component: (await import("@/pages/guides/ApiaryMonitoring")).default,
  }),
  "beekeeper-app": async () => ({
    Component: (await import("@/pages/guides/BeekeeperApp")).default,
  }),
  "hive-weight": async () => ({
    Component: (await import("@/pages/guides/HiveWeight")).default,
  }),
  "iot-in-beekeeping": async () => ({
    Component: (await import("@/pages/guides/IotInBeekeeping")).default,
  }),
};

/** Build the per-language guide route records for a given lang prefix. */
const guideRoutesFor = (lang: "en" | "pl" | "de"): RouteRecord[] =>
  GUIDES.map((g) => ({
    path: g.slugs[lang],
    lazy: guideLazyLoaders[g.id],
  }));

// Teaser routes that exist in every language. Used to generate duplicates
// under the `/pl` and `/de` prefixes. Mirrors `ALL_TEASER_PATHS` in
// `@/lib/teaser-routes.ts` (which is the source of truth for `includedRoutes`
// and the sitemap). Keep the two in sync.
type LocalizedChild =
  | { path: ""; element: JSX.Element }
  | { path: string; lazy: () => Promise<{ Component: React.ComponentType }> }
  | { path: string; element: JSX.Element };

const teaserChildren: LocalizedChild[] = [
  { path: "", element: <Index /> },
  { path: "platform", lazy: lazyMobileApp },
  { path: "contact", lazy: lazyContact },
  { path: "terms", lazy: lazyTerms },
  { path: "privacy", lazy: lazyPrivacy },
  { path: "returns", lazy: lazyReturns },
  { path: "delete-app-account", lazy: lazyDeleteAppAccount },
  { path: "faq", lazy: lazyFaq },
  { path: "guides", lazy: lazyGuides },
];

// Functional (non-teaser) routes that must also be reachable under the
// `/pl` and `/de` prefixes so menu/CTA clicks from a localized page keep
// the language prefix in the URL. Not prerendered by SSG (see
// `includedRoutes` in `vite.config.ts`) - they load via SPA fallback.
// Auth-gated routes (account, register, oauth, checkout) are intentionally
// excluded: they are not "menu links" and localizing them expands scope.
const functionalChildren: LocalizedChild[] = [
  { path: "service-terms", lazy: async () => ({ Component: (await import("@/pages/ServiceTerms")).default }) },
  {
    // QR-code landing that bounces visitors to the correct mobile app
    // store based on their UA (or to `/platform` on desktop). Pure client-side
    // redirect - never prerendered.
    path: "download",
    lazy: lazyAppRedirect,
  },
  {
    path: "products",
    lazy: async () => ({ Component: (await import("@/pages/Products")).default }),
  },
  {
    // Dedicated marketing landing for the hive scale platform extension.
    // Must come BEFORE `products/:id` so it takes precedence over the
    // dynamic route for this specific id.
    path: "products/scale",
    lazy: async () => ({ Component: (await import("@/pages/HiveScale")).default }),
  },
  {
    // Keep the 2G offer distinct from the default 4G marketing page.
    path: "products/scale-2g",
    lazy: async () => ({ Component: (await import("@/pages/HiveScale")).HiveScale2G }),
  },
  {
    path: "products/nfc-tag",
    lazy: async () => ({ Component: (await import("@/pages/NFCTag")).default }),
  },
  {
    // Dedicated coming-soon marketing landing for the Fluxabee Recorder.
    // Must come BEFORE `products/:id` so it takes precedence.
    path: "products/recorder",
    lazy: async () => ({ Component: (await import("@/pages/Recorder")).default }),
  },
  {
    path: "products/:id",
    lazy: async () => ({ Component: (await import("@/pages/ProductDetail")).default }),
  },
  {
    path: "manual/scale",
    lazy: async () => ({ Component: (await import("@/pages/ManualScale")).default }),
  },
  {
    path: "p/:code",
    lazy: async () => ({ Component: (await import("@/pages/ProductDeepLink")).default }),
  },
  {
    path: "h/:id",
    lazy: async () => ({ Component: (await import("@/pages/HiveDeepLink")).default }),
  },
  {
    path: "n/:code",
    lazy: async () => ({ Component: (await import("@/pages/NfcDeepLink")).default }),
  },
  {
    path: "cart",
    lazy: async () => ({ Component: (await import("@/pages/Cart")).default }),
  },
];

const toRouteRecord = (c: LocalizedChild): RouteRecord => {
  if (c.path === "") return { index: true, element: (c as { element: JSX.Element }).element };
  if ("lazy" in c) return { path: c.path, lazy: c.lazy };
  return { path: c.path, element: (c as { element: JSX.Element }).element };
};

// Auth-gated routes (account/*) live only on top-level. If a user lands on
// /pl/account or /de/account/orders we redirect them to the un-prefixed
// equivalent instead of showing 404.
const RedirectAccountToTopLevel = () => {
  const params = useParams();
  const rest = params["*"];
  return <Navigate to={`/account${rest ? `/${rest}` : ""}`} replace />;
};

// The platform page used to live at `/app`. Vercel issues a 301 for the old
// URL (see `vercel.json`), this client-side fallback covers dev/preview and
// in-app navigation. Not part of `TEASER_LOGICAL_PATHS` - never prerendered
// or listed in the sitemap.
const RedirectAppToPlatform = () => {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={{ pathname: pathname.replace(/\/app\/?$/, "/platform"), search, hash }} replace />;
};

const localizedTeaserRoutes = (lang: "pl" | "de"): RouteRecord[] => [
  {
    path: lang,
    element: <LangRoute lang={lang} />,
    children: [
      ...teaserChildren.map(toRouteRecord),
      ...functionalChildren.map(toRouteRecord),
      // Long-form SEO guides - slugs differ per language (e.g. PL
      // `poradnik/...`, DE `ratgeber/...`), so they're attached here
      // rather than in `teaserChildren`.
      ...guideRoutesFor(lang),
      { path: "app", element: <RedirectAppToPlatform /> },
      { path: "account", element: <RedirectAccountToTopLevel /> },
      { path: "account/*", element: <RedirectAccountToTopLevel /> },
      { path: "*", element: <NotFound /> },
    ],
  },
];

export const routes: RouteRecord[] = [
  {
    path: "/",
    element: <Layout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      // EN teaser routes - no prefix (EN is the main locale).
      { index: true, element: <Index /> },
      { path: "platform", lazy: lazyMobileApp },
      { path: "app", element: <RedirectAppToPlatform /> },
      { path: "contact", lazy: lazyContact },
      { path: "terms", lazy: lazyTerms },
      { path: "privacy", lazy: lazyPrivacy },
      { path: "returns", lazy: lazyReturns },
      { path: "delete-app-account", lazy: lazyDeleteAppAccount },
      { path: "faq", lazy: lazyFaq },
      { path: "guides", lazy: lazyGuides },

      // EN long-form SEO guides at site root (e.g. `/guide/...`).
      ...guideRoutesFor("en"),

      // PL teaser routes - /pl prefix
      ...localizedTeaserRoutes("pl"),

      // DE teaser routes - /de prefix
      ...localizedTeaserRoutes("de"),

      // Functional routes - also available at top-level (no prefix) for
      // EN users. Loaded lazily so SSG never prerenders them and they
      // don't bloat the initial bundle. Same list is spread into each
      // `/pl` and `/de` subtree via `localizedTeaserRoutes` so that
      // localized nav links (e.g. `/pl/products`) resolve correctly.
      ...functionalChildren.map(toRouteRecord),
      {
        path: "register",
        lazy: async () => ({ Component: (await import("@/pages/Register")).default }),
      },
      {
        path: "oauth/callback",
        lazy: async () => ({ Component: (await import("@/pages/OAuthCallback")).default }),
      },
      {
        path: "checkout/success",
        lazy: async () => ({ Component: (await import("@/pages/CheckoutSuccess")).default }),
      },
      {
        path: ":locale/checkout/success",
        lazy: async () => ({ Component: (await import("@/pages/CheckoutSuccess")).default }),
      },
      accountRoute,
      // Catch-all - loaded eagerly so SSG can render a proper 404 shell
      // when the SPA fallback HTML is served for any non-prerendered path.
      { path: "*", element: <NotFound /> },
    ],
  },
];

export default routes;
